import { useEffect, useState } from "react";
import api, { formatINR, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Check, X, Pencil, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

export default function Approvals() {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [selectedPhones, setSelectedPhones] = useState({}); // campaign_id -> Set of phones
  const [active, setActive] = useState(null);
  const [action, setAction] = useState("approve");
  const [editedMsg, setEditedMsg] = useState("");
  const [reason, setReason] = useState("");

  const load = async () => setItems((await api.get("/whatsapp/approval-queue")).data);
  useEffect(() => { load(); }, []);

  const toggleExpand = (cid) => setExpanded({ ...expanded, [cid]: !expanded[cid] });

  const togglePhone = (cid, phone, all = false, recipients = []) => {
    const cur = selectedPhones[cid] ? new Set(selectedPhones[cid]) : new Set();
    if (all) {
      if (cur.size === recipients.length) {
        setSelectedPhones({ ...selectedPhones, [cid]: new Set() });
      } else {
        setSelectedPhones({ ...selectedPhones, [cid]: new Set(recipients.map((r) => r.phone)) });
      }
    } else {
      if (cur.has(phone)) cur.delete(phone); else cur.add(phone);
      setSelectedPhones({ ...selectedPhones, [cid]: cur });
    }
  };

  const act = async () => {
    try {
      const body = { action, reason };
      if (action === "edit_approve") body.edited_message = editedMsg;
      if (action === "approve_selected") {
        const sel = selectedPhones[active.id];
        if (!sel || sel.size === 0) { toast.error("No customers selected"); return; }
        body.selected_phones = Array.from(sel);
      }
      const { data } = await api.post(`/whatsapp/campaigns/${active.id}/action`, body);
      toast.success(`${action === "reject" ? "Rejected" : "Sent"}: ${data.sent_count || 0}, skipped ${data.skipped_count || 0}`);
      setActive(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openDialog = (c, a) => {
    setActive(c); setAction(a); setEditedMsg(c.message); setReason("");
  };

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <div className="text-sm text-slate-500">All cost-incurring actions (WhatsApp campaigns, bulk messages) require your approval. Trigger campaigns are batched — one entry per run, expand to see all customers.</div>
      <div className="space-y-4">
        {items.length === 0 && <div className="text-slate-500 bg-white border border-slate-200 p-8 text-center rounded-sm">No pending approvals.</div>}
        {items.map((c) => {
          const isBatch = c.is_batch || (c.batch_recipients && c.batch_recipients.length > 0);
          const recipients = c.batch_recipients || [];
          const sel = selectedPhones[c.id] || new Set();
          const isExp = !!expanded[c.id];
          return (
            <div key={c.id} className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`approval-${c.id}`}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500">WhatsApp Campaign</div>
                    {isBatch && <span className="text-[10px] uppercase tracking-widest bg-blue-100 text-blue-800 px-2 py-0.5">Batch</span>}
                  </div>
                  <div className="font-semibold text-slate-900 text-lg mt-1">{c.name}</div>
                  <div className="text-xs text-slate-500 mt-1">Requested by <span className="font-medium">{c.requested_by_name}</span> · {new Date(c.created_at).toLocaleString("en-IN")}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Nearby Estimate</div>
                  <div className="text-2xl font-bold tabular-nums">{formatINR(c.estimated_cost)}</div>
                  <div className="text-xs text-slate-500">{c.estimated_recipients} messages · {c.kind || c.target?.replace("_", " ")}</div>
                </div>
              </div>
              <div className="bg-slate-50 border border-slate-200 p-3 text-sm mb-3 font-mono text-xs whitespace-pre-wrap">{c.message || "(per-customer rendered messages — expand to see)"}</div>

              {isBatch && (
                <div className="border border-slate-200 mb-3">
                  <div className="bg-slate-50 px-3 py-2 flex items-center justify-between border-b border-slate-200">
                    <button onClick={() => toggleExpand(c.id)} className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-700" data-testid={`expand-${c.id}`}>
                      {isExp ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      {recipients.length} customers · {sel.size} selected
                    </button>
                    {isExp && (
                      <Button variant="outline" size="sm" className="rounded-none h-7 text-xs" onClick={() => togglePhone(c.id, null, true, recipients)} data-testid={`select-all-${c.id}`}>
                        {sel.size === recipients.length ? "Deselect All" : "Select All"}
                      </Button>
                    )}
                  </div>
                  {isExp && (
                    <div className="max-h-72 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 border-b border-slate-200"><tr>
                          <th className="p-2 w-8"></th>
                          <th className="p-2 text-left">Customer</th>
                          <th className="p-2 text-left">Phone</th>
                          <th className="p-2 text-left">Message Preview</th>
                        </tr></thead>
                        <tbody>
                          {recipients.map((r, i) => (
                            <tr key={i} className="border-b border-slate-100" data-testid={`recipient-${c.id}-${i}`}>
                              <td className="p-2"><Checkbox checked={sel.has(r.phone)} onCheckedChange={() => togglePhone(c.id, r.phone)} /></td>
                              <td className="p-2 font-medium">{r.customer_name}</td>
                              <td className="p-2 text-slate-500 font-mono">{r.phone}</td>
                              <td className="p-2 text-slate-600 truncate max-w-md">{(r.message || "").slice(0, 100)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="outline" className="rounded-none border-red-300 text-red-600 hover:bg-red-50" onClick={() => openDialog(c, "reject")} data-testid={`reject-${c.id}`}><X className="w-4 h-4 mr-1" />Reject All</Button>
                {!isBatch && <Button variant="outline" className="rounded-none" onClick={() => openDialog(c, "edit_approve")} data-testid={`edit-approve-${c.id}`}><Pencil className="w-4 h-4 mr-1" />Edit & Approve</Button>}
                {isBatch && sel.size > 0 && sel.size < recipients.length && (
                  <Button className="bg-blue-600 hover:bg-blue-700 rounded-none" onClick={() => openDialog(c, "approve_selected")} data-testid={`approve-selected-${c.id}`}><Check className="w-4 h-4 mr-1" />Approve Selected ({sel.size})</Button>
                )}
                <Button className="bg-green-600 hover:bg-green-700 rounded-none" onClick={() => openDialog(c, "approve")} data-testid={`approve-${c.id}`}><Check className="w-4 h-4 mr-1" />Approve All & Send</Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle>
            {action === "approve" ? "Confirm Approval"
              : action === "approve_selected" ? "Approve Selected Customers"
              : action === "reject" ? "Reject Campaign" : "Edit & Approve"}
          </DialogTitle></DialogHeader>
          {action === "edit_approve" && (
            <div><Label>Edit message</Label><Textarea rows={5} value={editedMsg} onChange={(e) => setEditedMsg(e.target.value)} className="rounded-none mt-1" data-testid="edit-msg" /></div>
          )}
          <div><Label>{action === "reject" ? "Rejection reason" : "Notes"}</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-none mt-1" data-testid="action-reason" /></div>
          {action !== "reject" && active && (
            <div className="bg-amber-50 border border-amber-200 text-sm p-3 text-amber-800">
              Nearby Estimate: <b>{formatINR(active.estimated_cost)}</b> for {active.estimated_recipients} messages.
              {action === "approve_selected" && <div className="mt-1">Sending to <b>{(selectedPhones[active.id] || new Set()).size}</b> selected.</div>}
            </div>
          )}
          <DialogFooter><Button onClick={act} className={`rounded-none ${action === "reject" ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}`} data-testid="confirm-action">{action === "reject" ? "Reject" : "Confirm"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
